/**
 * TemplateCreationBody.jsx
 *
 * A unified main body component for creating templates (both Utility & Marketing),
 * depending on the "selectedCategory" from templateCreationSlice.
 */
import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  Box,
  Typography,
  TextField,
  Checkbox,
  FormControlLabel,
  Alert,
  RadioGroup,
  Radio,
  Button,
  Stack,
  CircularProgress,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { toast } from "react-hot-toast";
import { isSubscriptionBlockedError } from "../../../utils/subscriptionError";

import { useDispatch, useSelector } from "react-redux";

// Actions from our unified templateCreationSlice
import {
  updateFormData,
  updateFieldsFlagAndMsg,
  uploadTemplateMedia,
} from "../../../redux/Slice/Templates/templateCreationSlice";
import { fetchUserPermissions } from "../../../redux/Slice/User/userDetailsSlice";
import AuthView from "../../../utils/AuthView";

// Component that handles Buttons/Carousel conditionally
import TemplateButtonOptions from "./TemplateButtonOptions";

const TemplateCreationBody = () => {
  const dispatch = useDispatch();
  const {
    templateId,
    language,
    allowAutoCategoryChange,
    header,
    headerData,
    body,
    bodyVariables,
    footer,
    fieldsErrorMsg,
    buttons,
    buttonsMode,
    carouselCards,
    mediaUploadStatus,
    mediaUploadError,
    editMode,
  } = useSelector((state) => state.templateCreation);
  const selectedCategory = useSelector(
    (state) => state.templateCreation.selectedCategory
  );
  const { userPermissions } = useSelector((state) => state.userDetails);

  // Reference to the body TextField
  const bodyTextFieldRef = useRef(null);

  // Check if user has permission to add templates
  const canAddTemplates = userPermissions?.permissions?.Templates?.Add || false;

  useEffect(() => {
    dispatch(fetchUserPermissions());
  }, [dispatch]);

  // Local state to track validity of certain required fields
  const [templateIdError, setTemplateIdError] = useState(false);
  const [templateIdTouched, setTemplateIdTouched] = useState(false);
  const [bodyError, setBodyError] = useState(false);
  const [bodyTouched, setBodyTouched] = useState(false);
  // Track if a file was successfully uploaded
  const [mediaUploadSuccess, setMediaUploadSuccess] = useState(false);

  // ====== Shared change helper ======
  const handleChange = useCallback((key, value) => {
    dispatch(updateFormData({ key, value }));
  }, [dispatch]);

  // ====== Template ID Handling ======
  const handleTemplateIdChange = (e) => {
    const value = e.target.value;

    // Example constraint: only a-z0-9_ up to 512 chars
    if (!/^[0-9a-z_]*$/.test(value)) {
      return; // reject invalid characters
    }
    if (value.length > 512) {
      return; // reject if too long
    }

    handleChange("templateId", value);
  };

  // Watch templateId and set local error - but only if the field has been touched
  useEffect(() => {
    if (templateIdTouched) {
      setTemplateIdError(!templateId.trim());
    }
  }, [templateId, templateIdTouched]);

  // ====== Body Handling ======
  const handleBodyChange = (e) => {
    const value = e.target.value;
    handleChange("body", value);
    if (bodyTouched) {
      setBodyError(!value.trim());
    }
  };

  // Handle when body field gets focus
  const handleBodyFocus = () => {
    setBodyTouched(true);
  };

  // Handle file uploads for header (image/video/document)
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = [
      "image/jpeg",
      "image/png",
      "video/mp4",
      "application/pdf",
    ];
    if (!allowedTypes.includes(file.type)) {
      toast.error(
        "Invalid file type. Please upload jpg, png, mp4, or pdf files only."
      );
      return;
    }

    // Validate file size (10MB limit)
    const maxSize = 10 * 1024 * 1024; // 10MB in bytes
    if (file.size > maxSize) {
      toast.error("File size should not exceed 10MB");
      return;
    }

    try {
      const formData = new FormData();
      formData.append("file", file);

      // Dispatch the upload action
      const resultAction = await dispatch(
        uploadTemplateMedia(formData)
      ).unwrap();

      if (resultAction && resultAction.header) {
        // Update the header component with the URL
        handleChange("headerData", resultAction.header);
        setMediaUploadSuccess(true);
        toast.success("File uploaded successfully");
      } else {
        toast.error("Failed to get upload URL");
      }
    } catch (error) {
      console.error("Upload error:", error);
      if (!isSubscriptionBlockedError(error)) {
        toast.error(error.message || "Failed to upload file");
      }
    }
  };

  // ====== Variable Insertion Handling ======
  const handleAddVariable = () => {
    // Find the highest variable number currently in use
    const regex = /{{(\d+)}}/g;
    const matches = [...body.matchAll(regex)];
    const variableNumbers = matches.map((match) => parseInt(match[1], 10));

    // Determine the next number (if no variables yet, start with 1)
    const nextNumber =
      variableNumbers.length > 0 ? Math.max(...variableNumbers) + 1 : 1;

    // Get the current cursor position in the text field
    const textField = bodyTextFieldRef.current;
    if (!textField) return;

    const inputElement = textField.querySelector("textarea");
    if (!inputElement) return;

    const cursorPosition = inputElement.selectionStart;

    // Insert the variable at cursor position
    const variableToInsert = `{{${nextNumber}}}`;
    const newBody =
      body.substring(0, cursorPosition) +
      variableToInsert +
      body.substring(cursorPosition);

    // Update the body text
    handleChange("body", newBody);

    // Focus the text field again
    setTimeout(() => {
      if (inputElement) {
        inputElement.focus();
        // Set the cursor position after the inserted variable
        const newPosition = cursorPosition + variableToInsert.length;
        inputElement.setSelectionRange(newPosition, newPosition);
      }
    }, 0);
  };

  // Generate variable options (1-10)
  // const variableOptions = Array.from({ length: 10 }, (_, i) => i + 1);

  // Example: keep track of placeholders {{1}}, {{2}}, etc. => bodyVariables
  useEffect(() => {
    const regex = /{{(\d+)}}/g;
    const variableIdsInBody = [...body.matchAll(regex)].map((m) =>
      parseInt(m[1], 10)
    );

    // Build a fresh array of bodyVariables
    // If we already have them, keep their "value" if the ID matches
    const updatedBodyVariables = variableIdsInBody.map((id) => {
      const existing = bodyVariables.find((v) => v.id === id);
      return existing || { id, value: "" };
    });

    // If there's a difference, update them
    if (
      JSON.stringify(bodyVariables) !== JSON.stringify(updatedBodyVariables)
    ) {
      handleChange("bodyVariables", updatedBodyVariables);
    }
  }, [body, bodyVariables, handleChange]);

  // ====== Comprehensive Field Validation => fieldsFlag & fieldsErrorMsg ======
  useEffect(() => {
    // 1) Check template ID
    const templateIdFilled = !templateIdError && templateId.trim() !== "";

    // 2) Check body
    const bodyFilled = !bodyError && body.trim() !== "";

    // 3) Check body variables (if needed):
    //    If bodyVariables must all be filled, check them:
    let variablesFilled = true;
    if (bodyVariables?.length) {
      variablesFilled = bodyVariables.every((v) => v.value.trim() !== "");
    }

    // 4) Check header:
    //    If header != "none", then headerData must be non‐empty
    let headerDataFilled = true;
    if (header !== "none") {
      headerDataFilled = headerData && headerData.trim() !== "";
    }

    // 5) Check button validation based on selected button types
    let buttonValidationPassed = true;
    const buttonErrors = [];

    // Check Quick Reply buttons
    buttons.quickReplies.forEach((reply, index) => {
      if (!reply.label || !reply.label.trim()) {
        buttonValidationPassed = false;
        buttonErrors.push(`• Quick Reply #${index + 1}: Label is required`);
      }
    });

    // Check Phone buttons
    buttons.phoneButtons.forEach((phoneBtn, index) => {
      if (!phoneBtn.label || !phoneBtn.label.trim()) {
        buttonValidationPassed = false;
        buttonErrors.push(`• Phone Button #${index + 1}: Label is required`);
      }
      if (!phoneBtn.countryCode || !phoneBtn.countryCode.trim()) {
        buttonValidationPassed = false;
        buttonErrors.push(`• Phone Button #${index + 1}: Country code is required`);
      }
      if (!phoneBtn.phoneNumber || !phoneBtn.phoneNumber.trim()) {
        buttonValidationPassed = false;
        buttonErrors.push(`• Phone Button #${index + 1}: Phone number is required`);
      }
      // Enhanced phone number format validation
      if (phoneBtn.countryCode && phoneBtn.phoneNumber) {
        const countryCode = phoneBtn.countryCode.trim();
        const phoneNumber = phoneBtn.phoneNumber.trim();
        
        // Check country code format
        if (!countryCode.startsWith('+')) {
          buttonValidationPassed = false;
          buttonErrors.push(`• Phone Button #${index + 1}: Country code must start with +`);
        } else if (!/^\+\d{1,4}$/.test(countryCode)) {
          buttonValidationPassed = false;
          buttonErrors.push(`• Phone Button #${index + 1}: Country code must be 1-4 digits after +`);
        }
        
        // Check phone number format
        if (!/^\d+$/.test(phoneNumber)) {
          buttonValidationPassed = false;
          buttonErrors.push(`• Phone Button #${index + 1}: Phone number must contain only digits`);
        } else if (phoneNumber.length < 7) {
          buttonValidationPassed = false;
          buttonErrors.push(`• Phone Button #${index + 1}: Phone number must be at least 7 digits`);
        } else if (phoneNumber.length > 15) {
          buttonValidationPassed = false;
          buttonErrors.push(`• Phone Button #${index + 1}: Phone number cannot exceed 15 digits`);
        }
        
        // Validate specific country code patterns
        const validCountryCodes = [
          '+1', '+7', '+20', '+27', '+30', '+31', '+32', '+33', '+34', '+36', '+39', '+40', '+41', '+43', '+44', '+45', '+46', '+47', '+48', '+49', '+51', '+52', '+54', '+55', '+56', '+57', '+58', '+60', '+61', '+62', '+63', '+64', '+65', '+66', '+81', '+82', '+84', '+86', '+90', '+91', '+92', '+93', '+94', '+95', '+98', '+212', '+213', '+216', '+218', '+220', '+221', '+222', '+223', '+224', '+225', '+226', '+227', '+228', '+229', '+230', '+231', '+232', '+233', '+234', '+235', '+236', '+237', '+238', '+239', '+240', '+241', '+242', '+243', '+244', '+245', '+246', '+247', '+248', '+249', '+250', '+251', '+252', '+253', '+254', '+255', '+256', '+257', '+258', '+260', '+261', '+262', '+263', '+264', '+265', '+266', '+267', '+268', '+269', '+290', '+291', '+297', '+298', '+299', '+350', '+351', '+352', '+353', '+354', '+355', '+356', '+357', '+358', '+359', '+370', '+371', '+372', '+373', '+374', '+375', '+376', '+377', '+378', '+380', '+381', '+382', '+383', '+385', '+386', '+387', '+389', '+420', '+421', '+423', '+500', '+501', '+502', '+503', '+504', '+505', '+506', '+507', '+508', '+509', '+590', '+591', '+592', '+593', '+594', '+595', '+596', '+597', '+598', '+599', '+670', '+672', '+673', '+674', '+675', '+676', '+677', '+678', '+679', '+680', '+681', '+682', '+683', '+685', '+686', '+687', '+688', '+689', '+690', '+691', '+692', '+850', '+852', '+853', '+855', '+856', '+880', '+886', '+960', '+961', '+962', '+963', '+964', '+965', '+966', '+967', '+968', '+970', '+971', '+972', '+973', '+974', '+975', '+976', '+977', '+994', '+995', '+996', '+998'
        ];
        
        if (!validCountryCodes.includes(countryCode)) {
          buttonValidationPassed = false;
          buttonErrors.push(`• Phone Button #${index + 1}: Invalid country code. Please use a valid international country code`);
        }
      }
    });

    // Check Link buttons
    buttons.linkButtons.forEach((linkBtn, index) => {
      if (!linkBtn.label || !linkBtn.label.trim()) {
        buttonValidationPassed = false;
        buttonErrors.push(`• Link Button #${index + 1}: Label is required`);
      }
      if (!linkBtn.link || linkBtn.link === "https://" || !linkBtn.link.trim()) {
        buttonValidationPassed = false;
        buttonErrors.push(`• Link Button #${index + 1}: URL is required`);
      }
      // Check if it's a dynamic URL and requires dynamic variable label
      if (linkBtn.isLinkDynamic && (!linkBtn.dynamicVariableLabel || !linkBtn.dynamicVariableLabel.trim())) {
        buttonValidationPassed = false;
        buttonErrors.push(`• Link Button #${index + 1}: Dynamic variable label is required for dynamic URLs`);
      }
      // Validate URL format for static URLs
      if (linkBtn.link && linkBtn.link !== "https://" && !linkBtn.isLinkDynamic) {
        const urlRegex = /^https:\/\/[a-zA-Z0-9][-a-zA-Z0-9]*((\.[a-zA-Z0-9][-a-zA-Z0-9]*)+)(:\d+)?(\/[-a-zA-Z0-9._~:/?#[\]@!$&'()*+,;=]*)?$/;
        if (!urlRegex.test(linkBtn.link)) {
          buttonValidationPassed = false;
          buttonErrors.push(`• Link Button #${index + 1}: URL must be a valid https:// URL`);
        }
      }
    });

    // Check Carousel cards (for Marketing templates)
    carouselCards.forEach((card, cardIndex) => {
      // Check if card has media uploaded
      if (card.header !== "none" && (!card.headerData || !card.headerData.trim())) {
        buttonValidationPassed = false;
        buttonErrors.push(`• Carousel Card #${cardIndex + 1}: Media upload is required`);
      }
      
      // Check card body
      if (!card.body || !card.body.trim()) {
        buttonValidationPassed = false;
        buttonErrors.push(`• Carousel Card #${cardIndex + 1}: Body content is required`);
      }

      // Check card body variables
      if (card.bodyVariables && card.bodyVariables.length > 0) {
        card.bodyVariables.forEach((variable, varIndex) => {
          if (!variable.value || !variable.value.trim()) {
            buttonValidationPassed = false;
            buttonErrors.push(`• Carousel Card #${cardIndex + 1} Variable #${varIndex + 1}: Value is required`);
          }
        });
      }

      // Check card buttons
      if (card.buttons && card.buttons.length > 0) {
        card.buttons.forEach((btn, btnIndex) => {
          if (!btn.label || !btn.label.trim()) {
            buttonValidationPassed = false;
            buttonErrors.push(`• Carousel Card #${cardIndex + 1} Button #${btnIndex + 1}: Label is required`);
          }
          
          if (btn.type === "link" && (!btn.link || btn.link === "https://" || !btn.link.trim())) {
            buttonValidationPassed = false;
            buttonErrors.push(`• Carousel Card #${cardIndex + 1} Button #${btnIndex + 1}: URL is required`);
          }
          
          if (btn.type === "phone") {
            if (!btn.countryCode || !btn.countryCode.trim()) {
              buttonValidationPassed = false;
              buttonErrors.push(`• Carousel Card #${cardIndex + 1} Button #${btnIndex + 1}: Country code is required`);
            }
            if (!btn.phoneNumber || !btn.phoneNumber.trim()) {
              buttonValidationPassed = false;
              buttonErrors.push(`• Carousel Card #${cardIndex + 1} Button #${btnIndex + 1}: Phone number is required`);
            }
            
            // Enhanced phone number format validation for carousel buttons
            if (btn.countryCode && btn.phoneNumber) {
              const countryCode = btn.countryCode.trim();
              const phoneNumber = btn.phoneNumber.trim();
              
              // Check country code format
              if (!countryCode.startsWith('+')) {
                buttonValidationPassed = false;
                buttonErrors.push(`• Carousel Card #${cardIndex + 1} Button #${btnIndex + 1}: Country code must start with +`);
              } else if (!/^\+\d{1,4}$/.test(countryCode)) {
                buttonValidationPassed = false;
                buttonErrors.push(`• Carousel Card #${cardIndex + 1} Button #${btnIndex + 1}: Country code must be 1-4 digits after +`);
              }
              
              // Check phone number format
              if (!/^\d+$/.test(phoneNumber)) {
                buttonValidationPassed = false;
                buttonErrors.push(`• Carousel Card #${cardIndex + 1} Button #${btnIndex + 1}: Phone number must contain only digits`);
              } else if (phoneNumber.length < 7) {
                buttonValidationPassed = false;
                buttonErrors.push(`• Carousel Card #${cardIndex + 1} Button #${btnIndex + 1}: Phone number must be at least 7 digits`);
              } else if (phoneNumber.length > 15) {
                buttonValidationPassed = false;
                buttonErrors.push(`• Carousel Card #${cardIndex + 1} Button #${btnIndex + 1}: Phone number cannot exceed 15 digits`);
              }
              
              // Validate specific country code patterns
              const validCountryCodes = [
                '+1', '+7', '+20', '+27', '+30', '+31', '+32', '+33', '+34', '+36', '+39', '+40', '+41', '+43', '+44', '+45', '+46', '+47', '+48', '+49', '+51', '+52', '+54', '+55', '+56', '+57', '+58', '+60', '+61', '+62', '+63', '+64', '+65', '+66', '+81', '+82', '+84', '+86', '+90', '+91', '+92', '+93', '+94', '+95', '+98', '+212', '+213', '+216', '+218', '+220', '+221', '+222', '+223', '+224', '+225', '+226', '+227', '+228', '+229', '+230', '+231', '+232', '+233', '+234', '+235', '+236', '+237', '+238', '+239', '+240', '+241', '+242', '+243', '+244', '+245', '+246', '+247', '+248', '+249', '+250', '+251', '+252', '+253', '+254', '+255', '+256', '+257', '+258', '+260', '+261', '+262', '+263', '+264', '+265', '+266', '+267', '+268', '+269', '+290', '+291', '+297', '+298', '+299', '+350', '+351', '+352', '+353', '+354', '+355', '+356', '+357', '+358', '+359', '+370', '+371', '+372', '+373', '+374', '+375', '+376', '+377', '+378', '+380', '+381', '+382', '+383', '+385', '+386', '+387', '+389', '+420', '+421', '+423', '+500', '+501', '+502', '+503', '+504', '+505', '+506', '+507', '+508', '+509', '+590', '+591', '+592', '+593', '+594', '+595', '+596', '+597', '+598', '+599', '+670', '+672', '+673', '+674', '+675', '+676', '+677', '+678', '+679', '+680', '+681', '+682', '+683', '+685', '+686', '+687', '+688', '+689', '+690', '+691', '+692', '+850', '+852', '+853', '+855', '+856', '+880', '+886', '+960', '+961', '+962', '+963', '+964', '+965', '+966', '+967', '+968', '+970', '+971', '+972', '+973', '+974', '+975', '+976', '+977', '+994', '+995', '+996', '+998'
              ];
              
              if (!validCountryCodes.includes(countryCode)) {
                buttonValidationPassed = false;
                buttonErrors.push(`• Carousel Card #${cardIndex + 1} Button #${btnIndex + 1}: Invalid country code. Please use a valid international country code`);
              }
            }
          }
        });
      }
    });

    // Gather all missing fields
    const missingFields = [];
    if (!templateIdFilled) missingFields.push("• Template ID is required");
    if (!bodyFilled) missingFields.push("• Body content is required");
    if (!variablesFilled)
      missingFields.push("• All body variable fields must have values");
    if (!headerDataFilled)
      missingFields.push("• Header content is required when a header type is selected");
    
    // Add button validation errors
    if (!buttonValidationPassed) {
      missingFields.push(...buttonErrors);
    }

    // Dispatch result
    if (missingFields.length > 0) {
      dispatch(
        updateFieldsFlagAndMsg({
          fieldsFlag: false,
          fieldsErrorMsg: missingFields.join("\n"),
        })
      );
    } else {
      dispatch(
        updateFieldsFlagAndMsg({
          fieldsFlag: true,
          fieldsErrorMsg: "",
        })
      );
    }
  }, [
    templateIdError,
    bodyError,
    templateId,
    body,
    bodyVariables,
    header,
    headerData,
    buttons,
    carouselCards,
    dispatch,
  ]);

  // RENDER
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: 2, sm: 2.5, md: 3, lg: 3, xl: 3 } }}>
      {/* If user doesn't have permission to add templates */}
      {!canAddTemplates ? (
        <AuthView />
      ) : (
        <>
          <Typography 
            variant="h6" 
            gutterBottom
            sx={{
              fontSize: { xs: "16px", sm: "18px", md: "20px", lg: "24px", xl: "24px" },
            }}
          >
            {selectedCategory === "Utility"
              ? "Utility Template Details"
              : selectedCategory === "Marketing"
              ? "Marketing Template Details"
              : "Authentication Template Details"}
          </Typography>

          {/* Show an alert if there's a fieldsErrorMsg */}
          {fieldsErrorMsg && <Alert severity="error">{fieldsErrorMsg}</Alert>}

        

          {/* Template ID */}
          <TextField
            label="Template ID *"
            placeholder="Allowed: a-z, 0-9, _ (Max 512)"
            fullWidth
            required
            disabled={editMode}
            sx={{
              maxWidth: { xs: "100%", sm: "100%", md: 400, lg: 400, xl: 400 },
              "& .MuiInputLabel-root": {
                fontSize: { xs: "12px", sm: "13px", md: "14px", lg: "14px", xl: "14px" },
              },
              "& .MuiInputBase-input": {
                fontSize: { xs: "12px", sm: "13px", md: "14px", lg: "14px", xl: "14px" },
              },
              "& .MuiFormHelperText-root": {
                fontSize: { xs: "10px", sm: "11px", md: "12px", lg: "12px", xl: "12px" },
              },
            }}
            value={templateId}
            onChange={handleTemplateIdChange}
            onFocus={() => setTemplateIdTouched(true)}
            onBlur={() => setTemplateIdTouched(true)}
            error={templateIdTouched && templateIdError}
            helperText={
              editMode
                ? "A template's name can't be changed once created"
                : templateIdTouched && templateIdError
                ? "Template ID is required and must contain only letters, numbers, and underscores."
                : "This field is required"
            }
          />
          <Stack 
            direction={{ xs: "column", sm: "column", md: "row", lg: "row", xl: "row" }} 
            spacing={{ xs: 1.5, sm: 2, md: 2, lg: 2, xl: 2 }}
          >
            <TextField
              label="Selected Category"
              value={selectedCategory || "—"}
              disabled
              fullWidth
              sx={{ 
                maxWidth: { xs: "100%", sm: "100%", md: 400, lg: 400, xl: 400 },
                "& .MuiInputLabel-root": {
                  fontSize: { xs: "12px", sm: "13px", md: "14px", lg: "14px", xl: "14px" },
                },
                "& .MuiInputBase-input": {
                  fontSize: { xs: "12px", sm: "13px", md: "14px", lg: "14px", xl: "14px" },
                },
              }}
            />
            <TextField
              label="Language"
              value={language}
              disabled
              fullWidth
              sx={{ 
                maxWidth: { xs: "100%", sm: "100%", md: 400, lg: 400, xl: 400 },
                "& .MuiInputLabel-root": {
                  fontSize: { xs: "12px", sm: "13px", md: "14px", lg: "14px", xl: "14px" },
                },
                "& .MuiInputBase-input": {
                  fontSize: { xs: "12px", sm: "13px", md: "14px", lg: "14px", xl: "14px" },
                },
              }}
            />
          </Stack>

          {/* Checkbox: allowAutoCategoryChange */}
          <FormControlLabel
            control={
              <Checkbox
                checked={allowAutoCategoryChange}
                onChange={(e) =>
                  handleChange("allowAutoCategoryChange", e.target.checked)
                }
                sx={{
                  "& .MuiSvgIcon-root": {
                    fontSize: { xs: "18px", sm: "20px", md: "24px", lg: "24px", xl: "24px" },
                  },
                }}
              />
            }
            label="Allow automatic category change"
            sx={{
              "& .MuiFormControlLabel-label": {
                fontSize: { xs: "12px", sm: "13px", md: "14px", lg: "14px", xl: "14px" },
              },
            }}
          />
            {/* Global Media Upload Loading Indicator */}
            {mediaUploadStatus === "loading" && (
            <Alert 
              severity="info" 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1,
                backgroundColor: 'rgba(25, 118, 210, 0.08)',
                border: '1px solid rgba(25, 118, 210, 0.2)'
              }}
            >
              <CircularProgress size={20} sx={{ color: 'primary.main' }} />
              <Typography variant="body2">
                Uploading media file... Please wait while we process your file.
              </Typography>
            </Alert>
          )}

          {/* Global Media Upload Error */}
          {mediaUploadStatus === "failed" && mediaUploadError && (
            <Alert severity="error">
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                Media Upload Failed:
              </Typography>
              <Typography variant="body2">
                {typeof mediaUploadError === "string"
                  ? mediaUploadError
                  : mediaUploadError?.userFriendlyMessage || 
                    mediaUploadError?.message ||
                    mediaUploadError?.details ||
                    "Failed to upload media file. Please try again."}
              </Typography>
            </Alert>
          )}

          {/* ==================== HEADER SECTION ==================== */}
          {/* Carousel templates don't support a top-level header or footer —
              Meta only allows BODY + CAROUSEL at the top level, with each
              card carrying its own image/video header (configured below in
              the Carousel Configuration section) — so this section (and the
              Footer section further down) is hidden in that mode rather than
              letting the user fill it in and only discover the conflict once
              Meta rejects the submission. */}
          {buttonsMode === "carousel" ? (
            <Alert severity="info">
              Carousel templates don't use a top-level header or footer —
              each card has its own image/video header instead, set below in
              the Carousel Configuration section.
            </Alert>
          ) : (
            <>
          <Typography
            variant="h6"
            sx={{
              fontSize: { xs: "16px", sm: "18px", md: "20px", lg: "24px", xl: "24px" },
            }}
          >
            Header
          </Typography>
          <RadioGroup
            value={header}
            onChange={(e) => {
              // Reset media upload success when header changes
              setMediaUploadSuccess(false);
              handleChange("header", e.target.value);
              handleChange("headerData", "");
            }}
            sx={{ flexDirection: "column", gap: 0 }}
          >
            <FormControlLabel value="none" control={<Radio />} label="None" />
            <FormControlLabel value="image" control={<Radio />} label="Image" />
            <FormControlLabel value="video" control={<Radio />} label="Video" />
            <FormControlLabel
              value="document"
              control={<Radio />}
              label="Document"
            />
            <FormControlLabel value="text" control={<Radio />} label="Text" />
          </RadioGroup>

          {/* Conditionally show the upload or text field */}
          {header === "image" && (
            <Box sx={{ 
              display: "flex", 
              alignItems: "center", 
              gap: { xs: 1, sm: 1.5, md: 2, lg: 2, xl: 2 },
              flexWrap: { xs: "wrap", sm: "nowrap", md: "nowrap", lg: "nowrap", xl: "nowrap" },
            }}>
              <Button 
                variant="contained" 
                component="label"
                sx={{
                  fontSize: { xs: "11px", sm: "12px", md: "14px", lg: "14px", xl: "14px" },
                  padding: { xs: "6px 12px", sm: "8px 16px", md: "10px 20px", lg: "10px 20px", xl: "10px 20px" },
                }}
              >
                Upload Image (png, jpg)
                <input
                  type="file"
                  accept=".png,.jpg"
                  hidden
                  onChange={handleFileUpload}
                />
              </Button>
              {mediaUploadSuccess && <CheckCircleIcon color="success" sx={{ fontSize: { xs: "18px", sm: "20px", md: "24px", lg: "24px", xl: "24px" } }} />}
            </Box>
          )}
          {header === "video" && (
            <Box sx={{ 
              display: "flex", 
              alignItems: "center", 
              gap: { xs: 1, sm: 1.5, md: 2, lg: 2, xl: 2 },
              flexWrap: { xs: "wrap", sm: "nowrap", md: "nowrap", lg: "nowrap", xl: "nowrap" },
            }}>
              <Button 
                variant="contained" 
                component="label"
                sx={{
                  fontSize: { xs: "11px", sm: "12px", md: "14px", lg: "14px", xl: "14px" },
                  padding: { xs: "6px 12px", sm: "8px 16px", md: "10px 20px", lg: "10px 20px", xl: "10px 20px" },
                }}
              >
                Upload Video (mp4)
                <input
                  type="file"
                  accept="video/mp4"
                  hidden
                  onChange={handleFileUpload}
                />
              </Button>
              {mediaUploadSuccess && <CheckCircleIcon color="success" sx={{ fontSize: { xs: "18px", sm: "20px", md: "24px", lg: "24px", xl: "24px" } }} />}
            </Box>
          )}
          {header === "document" && (
            <Box sx={{ 
              display: "flex", 
              alignItems: "center", 
              gap: { xs: 1, sm: 1.5, md: 2, lg: 2, xl: 2 },
              flexWrap: { xs: "wrap", sm: "nowrap", md: "nowrap", lg: "nowrap", xl: "nowrap" },
            }}>
              <Button 
                variant="contained" 
                component="label"
                sx={{
                  fontSize: { xs: "11px", sm: "12px", md: "14px", lg: "14px", xl: "14px" },
                  padding: { xs: "6px 12px", sm: "8px 16px", md: "10px 20px", lg: "10px 20px", xl: "10px 20px" },
                }}
              >
                Upload Document (pdf)
                <input
                  type="file"
                  accept="application/pdf"
                  hidden
                  onChange={handleFileUpload}
                />
              </Button>
              {mediaUploadSuccess && <CheckCircleIcon color="success" sx={{ fontSize: { xs: "18px", sm: "20px", md: "24px", lg: "24px", xl: "24px" } }} />}
            </Box>
          )}
          {header === "text" && (
            <TextField
              label="Enter Header Text *"
              variant="outlined"
              fullWidth
              required
              placeholder="Enter header text content..."
              value={headerData || ""}
              onChange={(e) => handleChange("headerData", e.target.value)}
              sx={{ 
                marginTop: { xs: 1.5, sm: 2, md: 2, lg: 2, xl: 2 },
                "& .MuiInputLabel-root": {
                  fontSize: { xs: "12px", sm: "13px", md: "14px", lg: "14px", xl: "14px" },
                },
                "& .MuiInputBase-input": {
                  fontSize: { xs: "12px", sm: "13px", md: "14px", lg: "14px", xl: "14px" },
                },
                "& .MuiFormHelperText-root": {
                  fontSize: { xs: "10px", sm: "11px", md: "12px", lg: "12px", xl: "12px" },
                },
              }}
              helperText="Header text is required when text header is selected"
            />
          )}
            </>
          )}

          {/* Body */}
          <Typography 
            variant="subtitle1" 
            fontWeight="bold"
            sx={{
              fontSize: { xs: "14px", sm: "15px", md: "16px", lg: "16px", xl: "16px" },
            }}
          >
            Body *
          </Typography>
          <TextField
            multiline
            minRows={4}
            fullWidth
            required
            label="Template Body"
            placeholder="Enter your template message here..."
            value={body}
            onChange={handleBodyChange}
            onFocus={handleBodyFocus}
            error={bodyTouched && bodyError}
            helperText={
              bodyTouched && bodyError
                ? "Body is required and cannot be empty"
                : "This field is required"
            }
            ref={bodyTextFieldRef}
            sx={{
              "& .MuiInputLabel-root": {
                fontSize: { xs: "12px", sm: "13px", md: "14px", lg: "14px", xl: "14px" },
              },
              "& .MuiInputBase-root": {
                alignItems: "flex-start",
              },
              "& .MuiInputBase-input": {
                fontSize: { xs: "12px", sm: "13px", md: "14px", lg: "14px", xl: "14px" },
                maxHeight: "100px",
                overflowY: "auto !important",
              },
              "& .MuiFormHelperText-root": {
                fontSize: { xs: "10px", sm: "11px", md: "12px", lg: "12px", xl: "12px" },
              },
            }}
          />

          {/* Show how many chars used */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography 
              variant="caption" 
              color="textSecondary"
              sx={{
                fontSize: { xs: "10px", sm: "11px", md: "12px", lg: "12px", xl: "12px" },
              }}
            >
              {body.length}/1024
            </Typography>

            {/* Add Variable Button */}
            <Button
              variant="outlined"
              size="small"
              onClick={handleAddVariable}
              startIcon={<AddCircleOutlineIcon />}
              sx={{
                fontSize: { xs: "11px", sm: "12px", md: "14px", lg: "14px", xl: "14px" },
                padding: { xs: "4px 8px", sm: "5px 10px", md: "6px 12px", lg: "6px 12px", xl: "6px 12px" },
                "& .MuiButton-startIcon": {
                  marginRight: { xs: "4px", sm: "6px", md: "8px", lg: "8px", xl: "8px" },
                  "& > *:nth-of-type(1)": {
                    fontSize: { xs: "16px", sm: "18px", md: "20px", lg: "20px", xl: "20px" },
                  },
                },
              }}
            >
              Add Body Variable
            </Button>
          </Box>

          {/* If we want to show the user the placeholders they have, we can do so. */}
          {bodyVariables && bodyVariables.length > 0 && (
            <Box>
              <Typography 
                variant="subtitle2" 
                sx={{ 
                  mt: { xs: 1.5, sm: 2, md: 2, lg: 2, xl: 2 },
                  fontSize: { xs: "12px", sm: "13px", md: "14px", lg: "14px", xl: "14px" },
                }}
              >
                Variables Found in Body:
              </Typography>
              {bodyVariables.map((v) => (
                <Box
                  key={v.id}
                  sx={{ 
                    display: "flex", 
                    flexDirection: { xs: "column", sm: "row", md: "row", lg: "row", xl: "row" },
                    alignItems: { xs: "stretch", sm: "center", md: "center", lg: "center", xl: "center" },
                    gap: { xs: 1, sm: 1, md: 1, lg: 1, xl: 1 }, 
                    mt: { xs: 1, sm: 1, md: 1, lg: 1, xl: 1 }
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: { xs: "11px", sm: "12px", md: "14px", lg: "14px", xl: "14px" },
                      minWidth: { xs: "auto", sm: "120px", md: "140px", lg: "140px", xl: "140px" },
                    }}
                  >
                    Value for {`{${v.id}}}`}
                  </Typography>
                  <TextField
                    size="small"
                    value={v.value}
                    fullWidth
                    onChange={(e) => {
                      // update that specific variable
                      const newVars = bodyVariables.map((varItem) =>
                        varItem.id === v.id
                          ? { ...varItem, value: e.target.value }
                          : varItem
                      );
                      handleChange("bodyVariables", newVars);
                    }}
                    sx={{
                      "& .MuiInputLabel-root": {
                        fontSize: { xs: "12px", sm: "13px", md: "14px", lg: "14px", xl: "14px" },
                      },
                      "& .MuiInputBase-input": {
                        fontSize: { xs: "12px", sm: "13px", md: "14px", lg: "14px", xl: "14px" },
                      },
                    }}
                  />
                </Box>
              ))}
            </Box>
          )}

          {/* Footer — like Header, Meta doesn't allow a top-level Footer on a
              Carousel template's main message bubble (BODY + CAROUSEL only),
              so it's hidden here too when Carousel is selected. */}
          {buttonsMode !== "carousel" && (
            <>
              <Typography
                variant="subtitle1"
                fontWeight="bold"
                sx={{
                  mt: { xs: 1.5, sm: 2, md: 2, lg: 2, xl: 2 },
                  fontSize: { xs: "14px", sm: "15px", md: "16px", lg: "16px", xl: "16px" },
                }}
              >
                Footer
              </Typography>
              <TextField
                fullWidth
                placeholder="E.g. 'Powered by MyApp'"
                value={footer}
                onChange={(e) => handleChange("footer", e.target.value)}
                sx={{
                  "& .MuiInputLabel-root": {
                    fontSize: { xs: "12px", sm: "13px", md: "14px", lg: "14px", xl: "14px" },
                  },
                  "& .MuiInputBase-input": {
                    fontSize: { xs: "12px", sm: "13px", md: "14px", lg: "14px", xl: "14px" },
                  },
                }}
              />
            </>
          )}

          {/* A single button options component that detects utility vs marketing */}
          <TemplateButtonOptions />
        </>
      )}
    </Box>
  );
};

export default TemplateCreationBody;
